// src/components/common/TableCustomizeDrawer.tsx
// Shared "Customize" right-side drawer for data tables.
// Controls column visibility + reorder, density, and number format.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import type { Density, TableColumnDef, TableCustomization } from './tableCustomization';

interface TableCustomizeDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  columnDefs: TableColumnDef[];
  draft: TableCustomization;
  updateDraft: (patch: Partial<TableCustomization>) => void;
  onApply: () => void;
  onResetToDefault: () => void;
  onCancel: () => void;
}

export default function TableCustomizeDrawer(props: TableCustomizeDrawerProps) {
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!showToast) return;
    const t = setTimeout(() => setShowToast(false), 4000);
    return () => clearTimeout(t);
  }, [showToast]);

  if (!props.open && !showToast) return null;

  const handleCancel = () => { props.onCancel(); props.onClose(); };
  const handleApply = () => {
    props.onApply();
    props.onClose();
    setShowToast(true);
  };

  const toggleColumn = (key: string) => {
    props.updateDraft({
      columns: props.draft.columns.map((c) => c.key === key ? { ...c, visible: !c.visible } : c),
    });
  };
  const moveColumn = (key: string, dir: -1 | 1) => {
    const sorted = [...props.draft.columns].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((c) => c.key === key);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const tmp = sorted[idx].order;
    sorted[idx].order = sorted[swapIdx].order;
    sorted[swapIdx].order = tmp;
    props.updateDraft({ columns: sorted });
  };

  const defMap = Object.fromEntries(props.columnDefs.map((d) => [d.key, d]));
  const sortedCols = [...props.draft.columns].sort((a, b) => a.order - b.order);

  const densities: Array<{ value: Density; label: string }> = [
    { value: 'compact', label: 'Compact' },
    { value: 'standard', label: 'Standard' },
    { value: 'spacious', label: 'Spacious' },
  ];

  return createPortal(
    <>
      {props.open && (
        <div onClick={handleCancel} className="fixed inset-0 z-[1000] bg-black/30 flex justify-end">
          <div onClick={(e) => e.stopPropagation()} className="bg-white border-l border-line shadow-2xl w-full max-w-[400px] h-full flex flex-col">
            <div className="relative px-5 py-4 border-b border-lineSoft">
              <h4 className="text-[15px] font-extrabold text-ink text-center m-0">{props.title ?? 'Customize'}</h4>
              <button type="button" onClick={handleCancel} aria-label="Close" className="absolute right-4 top-4 text-ink3 cursor-pointer border-none bg-transparent hover:text-ink">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="text-[13px] font-bold text-ink mb-2">Columns</div>
              <div className="text-[11.5px] text-ink3 font-semibold mb-2">Click to show/hide. Use arrows to reorder.</div>
              {sortedCols.map((c, i, arr) => {
                const def = defMap[c.key];
                if (!def) return null;
                const canToggle = def.togglable !== false;
                return (
                  <div key={c.key} className="flex items-center gap-2 py-1.5 text-[12px] text-ink">
                    <button type="button" onClick={() => moveColumn(c.key, -1)} disabled={i === 0} className="text-ink4 border-none bg-transparent cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed hover:text-ink">
                      <ArrowUp size={13} strokeWidth={2} />
                    </button>
                    <button type="button" onClick={() => moveColumn(c.key, 1)} disabled={i === arr.length - 1} className="text-ink4 border-none bg-transparent cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed hover:text-ink">
                      <ArrowDown size={13} strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      onClick={() => canToggle && toggleColumn(c.key)}
                      disabled={!canToggle}
                      className={`w-3.5 h-3.5 border-[1.5px] rounded-sm inline-flex items-center justify-center text-white text-[10px] cursor-pointer disabled:cursor-not-allowed ${c.visible ? 'bg-ink border-ink' : 'bg-white border-line'}`}
                      aria-label={def.label}
                    >
                      {c.visible && '\u2713'}
                    </button>
                    <span className={canToggle ? '' : 'text-ink3'}>
                      {def.label}
                      {!canToggle && <span className="text-ink4 text-[10px] ml-1">(always on)</span>}
                    </span>
                  </div>
                );
              })}

              <div className="text-[13px] font-bold text-ink mb-2 mt-6">Density</div>
              <div className="flex gap-2">
                {densities.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => props.updateDraft({ density: d.value })}
                    className={`flex-1 py-2 rounded-md text-[11.5px] font-semibold border cursor-pointer ${props.draft.density === d.value ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink hover:bg-[#FAFBFB]'}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <div className="text-[13px] font-bold text-ink mb-2 mt-6">Number format</div>
              <label className="flex items-center gap-2 py-1 text-[12px] text-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={props.draft.numberFormat.hideCurrencySymbol}
                  onChange={(e) => props.updateDraft({ numberFormat: { ...props.draft.numberFormat, hideCurrencySymbol: e.target.checked } })}
                  className="accent-ink"
                />
                <span>Do not show currency symbol</span>
              </label>
              <label className="block text-[11px] font-semibold text-ink3 mt-3 mb-1.5">Negative numbers</label>
              <div className="relative">
                <select
                  value={props.draft.numberFormat.negativeStyle}
                  onChange={(e) => props.updateDraft({ numberFormat: { ...props.draft.numberFormat, negativeStyle: e.target.value as 'parentheses' | 'minus' } })}
                  className="w-full bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink outline-none focus:border-ink appearance-none cursor-pointer pr-8"
                >
                  <option value="parentheses">(100)</option>
                  <option value="minus">-100</option>
                </select>
                <ChevronDown size={11} strokeWidth={2.5} className="text-ink3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div className="flex justify-between items-center gap-2 px-5 py-3.5 border-t border-lineSoft">
              <button type="button" onClick={props.onResetToDefault} className="text-teal-dark text-[12px] font-semibold border-none bg-transparent cursor-pointer hover:underline">
                Reset to default
              </button>
              <div className="flex gap-2 items-center">
                <button type="button" onClick={handleCancel} className="text-ink2 text-[13px] font-semibold px-3 py-2.5 border-none bg-transparent cursor-pointer hover:text-ink">
                  Cancel
                </button>
                <button type="button" onClick={handleApply} className="bg-ink text-white text-[13px] font-semibold px-4 py-2.5 rounded-[10px] shadow-sm border-none cursor-pointer hover:bg-ink2">
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showToast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 bg-ink text-white rounded-lg px-4 py-3 shadow-[0_10px_24px_rgba(14,26,26,0.18)] flex items-center gap-3 z-[1100] max-w-[400px]"
        >
          <span className="w-5 h-5 rounded-full bg-white text-ink inline-flex items-center justify-center text-[11px] font-extrabold">{'\u2713'}</span>
          <span className="text-[13px] font-medium leading-tight">Customize applied.</span>
          <button
            type="button"
            onClick={() => setShowToast(false)}
            aria-label="Dismiss"
            className="bg-transparent border-none cursor-pointer text-white/60 hover:text-white p-0 inline-flex items-center justify-center ml-auto"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </>,
    document.body
  );
}